/* eslint-disable complexity */
import store from 'src/redux/store';
import { AppError } from 'src/redux/types';
import { THETA_MARKETPLACE_API_ENDPOINT, THETA_AUTH_API_ENDPOINT, THETA_DATA_API_ENDPOINT, THETA_LEADERBOARD_API_ENDPOINT, THETA_REPORT_API_ENDPOINT, REFRESH_TOKEN_URL, THETAN_SUPPORT_API_ENDPOINT } from 'src/constants';
import { formatParamPath, formatQueryString } from './uri';
import { getSession, storeSession } from './session';
import { refreshTokenFailed } from '@profile/src/redux/remoteActions';
import { X_THETAN_SECRET_KEY } from '@wolffun/shared/constants';

/** refreshTokenAsync is a Promise to renew accessToken */
let refreshTokenAsync;

function getDefaultHeaders() {
  var _getSession;

  const accessToken = ((_getSession = getSession()) === null || _getSession === void 0 ? void 0 : _getSession.accessToken) || store.getState().userSignIn.accessToken;
  let headers = {
    accept: 'application/json',
    'Content-Type': 'application/json'
  };

  if (accessToken) {
    headers = { ...headers,
      Authorization: `Bearer ${accessToken}`
    };
  }

  return new Headers(headers);
}

export function makeAPICaller(type, path, onSuccess, onError) {
  // eslint-disable-next-line max-statements
  const exeAsync = async data => {
    var _getSession3;

    const fetcher = constructFetcher(type);
    const res = await fetcher(path, data);
    /**
     * - Check if the accessToken has expired or not.
     * - If the accessToken has expired, call the API refresh token.
     *  - If the response contains a new access token then recall the api before by `exeAsync` function;
     *  - If the refresh token failed then return {status: 'error', error: AppError.REFRESH_TOKEN_EXPIRED}
     */

    if (res && !res.success && res.code === AppError.TOKEN_EXPIRED) {
      if (!refreshTokenAsync) {
        var _getSession2;

        const fetcherRefreshToken = constructFetcher('theta_auth_post');
        refreshTokenAsync = fetcherRefreshToken(REFRESH_TOKEN_URL, {
          refreshToken: (_getSession2 = getSession()) === null || _getSession2 === void 0 ? void 0 : _getSession2.refreshToken
        });
      }

      const resRefresh = await refreshTokenAsync;
      refreshTokenAsync = null; // Reset refreshTokenAsync to null

      if (resRefresh.success) {
        const {
          accessToken,
          refreshToken
        } = resRefresh.data;

        if (accessToken && refreshToken) {
          storeSession(accessToken, refreshToken);
          return exeAsync(data);
        }
      }

      const errorRes = {
        status: resRefresh.status,
        error: resRefresh.code,
        debugMessage: resRefresh.message
      };
      const redirectUrl = location.pathname + location.search + location.hash;
      store.dispatch(refreshTokenFailed(errorRes.error, redirectUrl));
      return errorRes;
    }

    const accessToken = (_getSession3 = getSession()) === null || _getSession3 === void 0 ? void 0 : _getSession3.accessToken;

    if (accessToken && res && !res.success && res.code === AppError.TOKEN_INVALID) {
      const errorRes = {
        status: res.status,
        error: res.code,
        debugMessage: res.message
      };
      const redirectUrl = location.pathname + location.search + location.hash;
      store.dispatch(refreshTokenFailed(errorRes.error, redirectUrl));
      return errorRes;
    }

    if (type.startsWith('theta_marketplace')) {
      if (!res) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: AppError.UNNKOWN,
          debugMessage: 'empty response'
        };
      }

      if (!res.success) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: typeof res.code === 'number' ? res.code : AppError.UNNKOWN,
          debugMessage: res.message,
          body: res.errors
        };
      }

      return onSuccess(res.data, data);
    }

    if (type.startsWith('theta_auth')) {
      if (!res) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: AppError.UNNKOWN,
          debugMessage: 'empty response'
        };
      }

      if (!res.success) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: res.code,
          debugMessage: res.message,
          body: res.errors
        };
      }

      return onSuccess(res.data, data);
    }

    if (type.startsWith('theta_data')) {
      if (!res) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: AppError.UNNKOWN,
          debugMessage: 'empty response'
        };
      }

      if (!res.success) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: res.code || AppError.UNNKOWN,
          debugMessage: res.message,
          body: res.errors
        };
      }

      return onSuccess(res, data);
    }

    if (type.startsWith('theta_report')) {
      if (!res) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: AppError.UNNKOWN,
          debugMessage: 'empty response'
        };
      }

      if (!res.success) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: res.code,
          debugMessage: res.message,
          body: res.errors
        };
      }

      return onSuccess(res, data);
    }

    if (type.startsWith('theta_leaderboard')) {
      if (!res) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: AppError.UNNKOWN,
          debugMessage: 'empty response'
        };
      }

      if (!res.success) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: res.code,
          debugMessage: res.message,
          body: res.errors
        };
      }

      return onSuccess(res, data);
    }

    if (type.startsWith('admin_config')) {
      if (!res) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: AppError.UNNKOWN,
          debugMessage: 'empty response'
        };
      }

      if (res.success !== undefined && !res.success) {
        return onError ? onError(res, data) : {
          status: 'error',
          error: res.code,
          debugMessage: res.message,
          body: res.errors
        };
      }

      return onSuccess(res, data);
    }

    return {
      status: 'error',
      error: AppError.UNNKOWN,
      debugMessage: 'unhandled api type'
    };
  };

  return exeAsync;
}

function constructFetcher(type) {
  return async (path, data) => {
    const parsedPath = formatParamPath(path, data);

    switch (type) {
      case 'theta_marketplace_get':
        return await fetch(`${THETA_MARKETPLACE_API_ENDPOINT}${parsedPath}${formatQueryString(data)}`, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_marketplace_post':
        return await fetch(`${THETA_MARKETPLACE_API_ENDPOINT}${parsedPath}`, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          body: JSON.stringify(data),
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_marketplace_put':
        return await fetch(`${THETA_MARKETPLACE_API_ENDPOINT}${parsedPath}`, {
          method: 'PUT',
          mode: 'cors',
          cache: 'no-cache',
          body: JSON.stringify(data),
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_marketplace_delete':
        return await fetch(`${THETA_MARKETPLACE_API_ENDPOINT}${parsedPath}`, {
          method: 'DELETE',
          mode: 'cors',
          cache: 'no-cache',
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_auth_get':
        return await fetch(`${THETA_AUTH_API_ENDPOINT}${parsedPath}${formatQueryString(data)}`, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          headers: { ...getDefaultHeaders(),
            'X-ThetanSecretKey': X_THETAN_SECRET_KEY
          }
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_auth_post':
        return await fetch(`${THETA_AUTH_API_ENDPOINT}${parsedPath}`, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          body: JSON.stringify(data),
          headers: { ...getDefaultHeaders(),
            'X-ThetanSecretKey': X_THETAN_SECRET_KEY
          }
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_data_get':
        return await fetch(`${THETA_DATA_API_ENDPOINT}${parsedPath}${formatQueryString(data)}`, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_data_post':
        return await fetch(`${THETA_DATA_API_ENDPOINT}${parsedPath}`, {
          method: 'POST',
          mode: 'cors',
          cache: 'no-cache',
          body: JSON.stringify(data),
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_data_put':
        return await fetch(`${THETA_DATA_API_ENDPOINT}${parsedPath}`, {
          method: 'PUT',
          mode: 'cors',
          cache: 'no-cache',
          body: JSON.stringify(data),
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_leaderboard_get':
        return await fetch(`${THETA_LEADERBOARD_API_ENDPOINT}${parsedPath}${formatQueryString(data)}`, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);

      case 'theta_report_get':
        return await fetch(`${THETA_REPORT_API_ENDPOINT}${parsedPath}${formatQueryString(data)}`, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);

      case 'admin_config_get':
        return await fetch(`${THETAN_SUPPORT_API_ENDPOINT}${parsedPath}${formatQueryString(data)}`, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          headers: getDefaultHeaders()
        }).then(res => res.json()).catch(() => undefined);
    }

    return Promise.resolve(undefined);
  };
}